import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'

import i18n from '@/i18n'
import ExpensesService from '@/services/ExpensesService'
import LoadingIndicator from '@/widgets/LoadingIndicator'

const LOAD_FAILED_KEY = 'expenses.details.messages.loadFailed'

const currencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'PLN',
	maximumFractionDigits: 2,
})

const formatAmount = value => {
	if (typeof value !== 'number') return '—'
	return currencyFormatter.format(value)
}

export default function ExpenseDetails() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const [expense, setExpense] = useState(null)
	const [loading, setLoading] = useState(true)
	const expenseName = expense?.expenseName || t('expenses.list.table.untitled')

	const paymentMethodLabel = useCallback(value => {
		if (!value) return '—'
		return i18n.t(`expenses.form.paymentOptions.${value}`, { defaultValue: value })
	}, [])

	const statusLabel = useCallback(value => {
		if (!value) return '—'
		return i18n.t(`expenses.form.statusOptions.${value}`, { defaultValue: value })
	}, [])

	const loadExpense = useCallback(async () => {
		setLoading(true)
		const { data, error } = await ExpensesService.getExpenseById(id)
		if (error) {
			Swal.fire(i18n.t('expenses.list.messages.errorTitle'), error || i18n.t(LOAD_FAILED_KEY), 'error')
			navigate('/ksiegowosc/expenses')
			return
		}
		setExpense(data)
		setLoading(false)
	}, [id, navigate])

	useEffect(() => {
		if (id) loadExpense()
	}, [id, loadExpense])

	const tags = useMemo(() => (Array.isArray(expense?.tags) ? expense.tags : []), [expense])

	const handleDelete = async () => {
		const expenseNameForDialog = expense?.expenseName || i18n.t('expenses.list.table.untitled')
		const confirm = await Swal.fire({
			title: i18n.t('expenses.details.deleteDialog.confirmTitle'),
			text: i18n.t('expenses.details.deleteDialog.confirmText', { name: expenseNameForDialog }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonText: i18n.t('expenses.list.deleteDialog.cancelButton'),
			confirmButtonText: i18n.t('expenses.list.deleteDialog.confirmButton'),
		})
		if (!confirm.isConfirmed) return

		const result = await ExpensesService.deleteExpenses([id])
		if (!result.success) {
			Swal.fire(i18n.t('expenses.list.messages.errorTitle'), result.error || i18n.t('expenses.details.messages.deleteFailed'), 'error')
			return
		}
		Swal.fire(
			i18n.t('expenses.list.deleteDialog.deletedTitle'),
			result.message || i18n.t('expenses.details.messages.deletedSuccess', { name: expenseNameForDialog }),
			'success'
		)
		navigate('/ksiegowosc/expenses')
	}

	if (loading) return <LoadingIndicator message={t('expenses.details.loading')} />

	if (!expense) return <div className="min-h-screen flex items-center justify-center">{t('expenses.details.noData')}</div>

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
			<div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">{expenseName}</h1>
					<p className="text-sm text-gray-600">
						{expense.supplierName || t('expenses.details.header.noSupplier')} · {expense.invoiceNumber || t('expenses.details.header.noInvoice')}
					</p>
				</div>
				<div className="flex gap-3">
					<Link
						to={`/ksiegowosc/expenses/edit/${id}`}
						className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
					>
						{t('expenses.list.table.edit')}
					</Link>
					<button
						onClick={handleDelete}
						className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
					>
						{t('expenses.list.actions.delete')}
					</button>
				</div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<DetailCard label={t('expenses.form.fields.total')} value={formatAmount(expense.expenseTotal ?? expense.expenseSubtotal)} />
				<DetailCard label={t('expenses.form.fields.taxPercent')} value={expense.expensePercentTax ?? '—'} />
				<DetailCard label={t('expenses.form.fields.taxValue')} value={formatAmount(expense.expenseTaxValue)} />
			</div>

			<div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
				<DetailCard label={t('expenses.form.fields.paymentMethod')} value={paymentMethodLabel(expense.paymentMethod)} />
				<DetailCard label={t('expenses.form.fields.status')} value={statusLabel(expense.status)} />
			</div>

			<div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
				<DetailCard label={t('expenses.form.fields.expenseDate')} value={expense.expenseDate ? expense.expenseDate.substring(0, 10) : '—'} />
				<DetailCard label={t('expenses.details.cards.created')} value={expense.created ? expense.created.substring(0, 10) : '—'} />
			</div>

			<div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm p-6">
				<h3 className="text-sm font-semibold text-gray-700 mb-2">{t('expenses.details.sections.flags')}</h3>
				<div className="flex gap-4 text-sm text-gray-800">
					<span className={`px-2 py-1 rounded ${expense.forVat ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
						{expense.forVat ? t('expenses.details.flags.vatEligible') : t('expenses.details.flags.vatNo')}
					</span>
					<span className={`px-2 py-1 rounded ${expense.forCorporateTax ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
						{expense.forCorporateTax ? t('expenses.details.flags.corporateTaxEligible') : t('expenses.details.flags.corporateTaxNo')}
					</span>
				</div>
			</div>

			<div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm p-6">
				<h3 className="text-sm font-semibold text-gray-700 mb-2">{t('expenses.details.sections.tags')}</h3>
				<div className="flex flex-wrap gap-2">
					{tags.length
						? tags.map(tag => (
							<span key={tag} className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-700 border border-slate-200">
								{tag}
							</span>
						))
						: <span className="text-sm text-gray-500">{t('expenses.list.table.noTags')}</span>}
				</div>
			</div>

			<div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm p-6">
				<h3 className="text-sm font-semibold text-gray-700 mb-2">{t('expenses.details.sections.remarks')}</h3>
				<p className="text-gray-800 whitespace-pre-line">{expense.remarks || '—'}</p>
			</div>
		</div>
	)
}

function DetailCard({ label, value }) {
	return (
		<div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
			<p className="text-sm text-gray-600">{label}</p>
			<p className="mt-1 text-base font-semibold text-gray-900">{value ?? '—'}</p>
		</div>
	)
}
